import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * Generic Structure Element Form Component
 * Adapts form fields based on bridge level configuration
 * 
 * @param {Object} props
 * @param {Object} bridge - Bridge object with levelLabels configuration
 * @param {number} levelNumber - Level number (1, 2, 3, ...)
 * @param {Object} element - Element data for edit mode (null for create)
 * @param {Object} parentElement - Parent element (null for top-level)
 * @param {Array} combinations - Corrosion damage combinations
 * @param {Array} directions - Directions list
 * @param {Function} onSave - Callback when form is submitted
 * @param {Function} onClose - Callback to close modal
 */
const StructureElementForm = ({
  bridge,
  levelNumber,
  element,
  parentElement,
  combinations = [],
  directions = [],
  levelConfig: propLevelConfig,
  onSave,
  onClose
}) => {
  // Use propLevelConfig if provided, otherwise fall back to bridge levelLabels
  const levelConfig = propLevelConfig || bridge?.levelLabels?.[`level${levelNumber}`] || {};
  const levelLabel = levelConfig.label || `Level ${levelNumber}`;
  const isEditMode = !!element;

  // Initialize form data
  const [formData, setFormData] = useState(() => {
    if (isEditMode && element) {
      return {
        elementCode: element.elementCode || element.element_code || '',
        elementName: element.elementName || element.element_name || '',
        direction: element.direction || '',
        position: element.position || '',
        totalArea: element.totalArea || element.total_area || null,
        corrosionDamageCombinationId: element.corrosionDamageCombinationId || element.corrosion_damage_combination_id || null,
        pid: element.pid || null,
        componentGroupId: element.componentGroupId || element.component_group_id || null,
        cid: element.cid || null,
        refCode: element.refCode || element.ref_code || null,
        lastRepairYear: element.lastRepairYear || element.last_repair_year || null,
        lastRepairDate: element.lastRepairDate || element.last_repair_date || null,
        lastProcess: element.lastProcess || element.last_process || null,
        coatingSystem: element.coatingSystem || element.coating_system || null,
        panelBayCount: element.panelBayCount || element.panel_bay_count || 1,
        spanType: element.spanType || element.span_type || null,
        repairSteps: element.repairSteps || element.repair_steps || null,
        confinedSpace: element.confinedSpace || element.confined_space || false,
        confinedSpaceMultiplier: element.confinedSpaceMultiplier || element.confined_space_multiplier || 1.0,
        metadata: element.metadata || null,
        currentDegradationPercent: element.currentDegradationPercent ?? element.current_degradation_percent ?? null
      };
    }

    // Default values for create mode
    return {
      elementCode: '',
      elementName: '',
      direction: '',
      position: '',
      totalArea: null,
      corrosionDamageCombinationId: null,
      pid: null,
      componentGroupId: null,
      cid: null,
      refCode: null,
      lastRepairYear: null,
      lastRepairDate: null,
      lastProcess: null,
      coatingSystem: null,
      panelBayCount: 1,
      spanType: null,
      repairSteps: null,
      confinedSpace: false,
      confinedSpaceMultiplier: 1.0,
      metadata: null,
      currentDegradationPercent: null
    };
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('Form submission - levelConfig:', levelConfig);
    console.log('Form submission - formData:', formData);
    
    const submitData = {
      bridgeId: bridge.id,
      levelNumber: levelNumber,
      elementCode: formData.elementCode,
      elementName: formData.elementName,
      // Preserve existing parentId when editing, otherwise use parentElement
      parentId: parentElement 
        ? parentElement.id 
        : (isEditMode && element 
          ? (element.parentId || element.parent_id || null)
          : null)
    };

    // Add fields based on level configuration
    if (levelNumber === 1 || levelConfig.requiresDirection) {
      submitData.direction = formData.direction;
      submitData.position = formData.position;
    }

    // Always include these fields if the level config requires them (even if null)
    // This allows backend validation to properly check them
    if (levelConfig.holdsAreaData) {
      submitData.totalArea = formData.totalArea !== null && formData.totalArea !== undefined 
        ? parseFloat(formData.totalArea) 
        : null;
      const pct = formData.currentDegradationPercent;
      submitData.currentDegradationPercent = (pct !== null && pct !== undefined && pct !== '') 
        ? (parseFloat(pct) >= 0 && parseFloat(pct) <= 100 ? parseFloat(pct) : null) 
        : null;
    }

    if (levelConfig.holdsExposureClassData) {
      submitData.corrosionDamageCombinationId = formData.corrosionDamageCombinationId || null;
    }

    // Add optional fields that might be needed (include if they have values or are explicitly set)
    if (formData.pid !== null && formData.pid !== undefined) submitData.pid = formData.pid;
    if (formData.componentGroupId !== null && formData.componentGroupId !== undefined) submitData.componentGroupId = formData.componentGroupId;
    if (formData.cid !== null && formData.cid !== undefined && formData.cid !== '') submitData.cid = formData.cid;
    if (formData.refCode !== null && formData.refCode !== undefined && formData.refCode !== '') submitData.refCode = formData.refCode;
    if (formData.lastRepairYear !== null && formData.lastRepairYear !== undefined) submitData.lastRepairYear = formData.lastRepairYear;
    if (formData.lastRepairDate !== null && formData.lastRepairDate !== undefined) submitData.lastRepairDate = formData.lastRepairDate;
    if (formData.lastProcess !== null && formData.lastProcess !== undefined && formData.lastProcess !== '') submitData.lastProcess = formData.lastProcess;
    if (formData.coatingSystem !== null && formData.coatingSystem !== undefined && formData.coatingSystem !== '') submitData.coatingSystem = formData.coatingSystem;
    if (formData.panelBayCount !== null && formData.panelBayCount !== undefined) submitData.panelBayCount = formData.panelBayCount;
    if (formData.spanType !== null && formData.spanType !== undefined && formData.spanType !== '') submitData.spanType = formData.spanType;
    if (formData.repairSteps !== null && formData.repairSteps !== undefined) submitData.repairSteps = formData.repairSteps;
    if (formData.confinedSpace !== undefined) submitData.confinedSpace = formData.confinedSpace;
    if (formData.confinedSpaceMultiplier !== null && formData.confinedSpaceMultiplier !== undefined) submitData.confinedSpaceMultiplier = formData.confinedSpaceMultiplier;
    if (formData.metadata !== null && formData.metadata !== undefined) submitData.metadata = formData.metadata;
    
    console.log('Form submission - final submitData:', submitData);

    onSave(submitData, isEditMode ? element.id : null);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit' : 'Add'} {levelLabel}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {parentElement && (
          <div className="breadcrumb">
            Parent: {parentElement.elementName || parentElement.element_name}
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Element Code */}
          <div className="form-group">
            <label>{levelLabel} Code *</label>
            <input
              type="text"
              value={formData.elementCode}
              onChange={(e) => handleChange('elementCode', e.target.value)}
              required
              placeholder={`e.g., ${levelLabel}-001`}
            />
          </div>

          {/* Element Name */}
          <div className="form-group">
            <label>{levelLabel} Name *</label>
            <input
              type="text"
              value={formData.elementName}
              onChange={(e) => handleChange('elementName', e.target.value)}
              required
              placeholder={`e.g., ${levelLabel} 1`}
            />
          </div>

          {/* Direction and Position (for Level 1 or if required) */}
          {(levelNumber === 1 || levelConfig.requiresDirection) && (
            <>
              <div className="form-group">
                <label>Direction *</label>
                <select
                  value={formData.direction}
                  onChange={(e) => handleChange('direction', e.target.value)}
                  required
                >
                  <option value="">Select Direction</option>
                  {directions.map(dir => (
                    <option key={dir.id} value={dir.directionCode || dir.direction_code}>
                      {dir.directionCode || dir.direction_code} - {dir.directionName || dir.direction_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Position *</label>
                <select
                  value={formData.position}
                  onChange={(e) => handleChange('position', e.target.value)}
                  required
                >
                  <option value="">Select Position</option>
                  <option value="Internal">Internal</option>
                  <option value="External">External</option>
                </select>
              </div>
            </>
          )}

          {/* Total Area (if level holds area data) */}
          {levelConfig.holdsAreaData && (
            <div className="form-group">
              <label>Total Area (m²) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.totalArea || ''}
                onChange={(e) => handleChange('totalArea', e.target.value ? parseFloat(e.target.value) : null)}
                required
                min="0"
              />
              <small>Total surface area in square meters</small>
            </div>
          )}

          {/* Initial degradation % (Year 1) - if level holds area data */}
          {levelConfig.holdsAreaData && (
            <div className="form-group">
              <label>Initial degradation (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.currentDegradationPercent ?? ''}
                onChange={(e) => handleChange('currentDegradationPercent', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="e.g. 1.5"
              />
              <small>Used as Year 1 condition in simulation. Leave empty to derive from last repair year.</small>
            </div>
          )}

          {/* Corrosion Damage Combination (if level holds exposure data) */}
          {levelConfig.holdsExposureClassData && (
            <div className="form-group">
              <label className="flex items-center gap-2">
                Corrosion Damage Combination *
                <span 
                  className="text-gray-400 cursor-help" 
                  title="Exposure class (Corrosion-Damage Combination) is stored at the element level. Different elements can have different exposure classes based on their location, orientation, and environmental conditions. For example, elements facing north might have different corrosion exposure than elements facing south."
                >
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </span>
              </label>
              <select
                value={formData.corrosionDamageCombinationId || ''}
                onChange={(e) => handleChange('corrosionDamageCombinationId', e.target.value ? parseInt(e.target.value) : null)}
                required
              >
                <option value="">Select Combination</option>
                {combinations.map(comb => (
                  <option key={comb.id} value={comb.id}>
                    {comb.combinationCode || comb.combination_code} - {comb.description || ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Note: Exposure class is defined per element, not per bridge. This allows different elements to have different corrosion/damage characteristics.
              </p>
            </div>
          )}

          {/* PID (for panel bays) */}
          {levelNumber === 3 && (
            <div className="form-group">
              <label>PID (Optional)</label>
              <input
                type="number"
                value={formData.pid || ''}
                onChange={(e) => handleChange('pid', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Panel Bay PID (leave empty if not needed)"
              />
              <small>PID must be globally unique. Leave empty if you don't need a specific PID value.</small>
            </div>
          )}

          {/* Component Group ID */}
          {levelNumber === 3 && (
            <div className="form-group">
              <label>Component Group ID</label>
              <input
                type="number"
                value={formData.componentGroupId || ''}
                onChange={(e) => handleChange('componentGroupId', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Component Group ID"
              />
            </div>
          )}

          {/* CID and Ref Code (for components) */}
          {levelNumber >= 4 && (
            <>
              <div className="form-group">
                <label>CID</label>
                <input
                  type="text"
                  value={formData.cid || ''}
                  onChange={(e) => handleChange('cid', e.target.value)}
                  placeholder="Component ID"
                />
              </div>

              <div className="form-group">
                <label>Reference Code</label>
                <input
                  type="text"
                  value={formData.refCode || ''}
                  onChange={(e) => handleChange('refCode', e.target.value)}
                  placeholder="Reference code"
                />
              </div>
            </>
          )}

          {/* Panel Bay Count (for spans) */}
          {levelNumber === 2 && (
            <div className="form-group">
              <label>Panel Bay Count</label>
              <input
                type="number"
                value={formData.panelBayCount || 1}
                onChange={(e) => handleChange('panelBayCount', parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>
          )}

          {/* Maintenance fields (optional for all levels) */}
          <div className="form-group">
            <label>Last Repair Year</label>
            <input
              type="number"
              value={formData.lastRepairYear || ''}
              onChange={(e) => handleChange('lastRepairYear', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="e.g., 2020"
            />
          </div>

          <div className="form-group">
            <label>Last Process</label>
            <input
              type="text"
              value={formData.lastProcess || ''}
              onChange={(e) => handleChange('lastProcess', e.target.value)}
              placeholder="e.g., Wash, Top Coat"
            />
          </div>

          <div className="form-group">
            <label>Coating System</label>
            <input
              type="text"
              value={formData.coatingSystem || ''}
              onChange={(e) => handleChange('coatingSystem', e.target.value)}
              placeholder="e.g., Zinc/Epoxy/Urethane"
            />
          </div>

          {/* Confined Space (optional) */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.confinedSpace}
                onChange={(e) => handleChange('confinedSpace', e.target.checked)}
              />
              Confined Space
            </label>
          </div>

          {formData.confinedSpace && (
            <div className="form-group">
              <label>Confined Space Multiplier</label>
              <input
                type="number"
                step="0.1"
                value={formData.confinedSpaceMultiplier || 1.0}
                onChange={(e) => handleChange('confinedSpaceMultiplier', parseFloat(e.target.value) || 1.0)}
                min="1.0"
              />
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StructureElementForm;

